/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  CSSProperties,
  MutableRefObject,
  useState,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { If, Then, When } from "react-if";
import { VariableSizeList as List } from "react-window";
import styled from "styled-components";
import {
  Checkbox,
  FlexContainer,
  Input,
  Button,
  Text,
  theme,
} from "@nordcloud/gnui";
import { FilterHeader } from "~/components";
import { searchDelay } from "~/components/SearchInput/const";
import { useSleep } from "~/hooks";
import { noop, trimText, isNil } from "~/tools";
import { SidebarItemWrapperGrid } from "../Styled";
import { useSelectableListWithInputsFilter, useResize } from "./hooks";
import { Mode, ListWithInputsSelections, ListWithInputsItem } from "./types";
import { getSelectedMode, unselectAll } from "./utils";

// label heigh + sublabel heigh  + padding top + padding bottom
const ROW_HEIGHT_WITH_SUBLABEL = 24 + 18 + 8 + 8;
// label heigh  + padding top + padding bottom
const ROW_HEIGHT = 24 + 8 + 8;
const MAX_LIST_HEIGHT = 300;
// input heigh  + padding top + padding bottom
const INPUT_HEIGHT = 42 + 8 + 8;
const ADD_INPUT_BUTTON_HEIGHT = 40 + 8 + 8;

const MIN_INPUT_LENGTH = 0;
const MAX_INPUT_LENGTH = 200;

type RowInputsProps = {
  index: number;
  initialInputs: string[];
  filteredOptions: ListWithInputsItem[];
  onChangeInput: (rowIndex: number, nextRowInputs: string[]) => void;
  onAddInput: (index: number, value?: string) => void;
  onRemoveInput: (rowIndex: number, inputIndex: number) => void;
};

function RowInputs({
  index,
  initialInputs,
  filteredOptions,
  onChangeInput,
  onAddInput,
  onRemoveInput,
}: RowInputsProps) {
  const [rowInputs, setRowInputs] = useState<string[]>(initialInputs);
  const { addInputButtonText, inputPlaceholderText, allowMultipleInputs } =
    filteredOptions[index];

  useEffect(() => {
    setRowInputs(initialInputs);
  }, [initialInputs]);

  const { sleep, cancelSleep } = useSleep();

  return (
    <div>
      {rowInputs.map((value, i) => {
        return (
          <FlexContainer
            key={`${index}-${i}`}
            padding={theme.spacing.spacing02}
          >
            <InputWrapper>
              <Input
                name=""
                value={value}
                placeholder={inputPlaceholderText}
                onChange={async (e) => {
                  cancelSleep();
                  setRowInputs((prev) =>
                    prev.map((input, inputIndex) =>
                      inputIndex === i ? e.target.value : input
                    )
                  );
                  if (e.target.value.length !== 0) {
                    await sleep(searchDelay);
                  }
                  onChangeInput(
                    index,
                    rowInputs.map((input, inputIndex) =>
                      inputIndex === i ? e.target.value : input
                    )
                  );
                }}
                showClearButton
                onClear={() => {
                  setRowInputs((prev) =>
                    prev.map((input, inputIndex) =>
                      inputIndex === i ? "" : input
                    )
                  );
                  onChangeInput(
                    index,
                    rowInputs.map((input, inputIndex) => {
                      return inputIndex === i ? "" : input;
                    })
                  );
                }}
                minLength={MIN_INPUT_LENGTH}
                maxLength={MAX_INPUT_LENGTH}
              />
            </InputWrapper>
            <When condition={rowInputs.length > 1}>
              <Button
                marginLeft={theme.spacing.spacing02}
                severity="low"
                icon="trash"
                onClick={() => {
                  const nextRowInputs = rowInputs.filter(
                    (_, inputIndex) => inputIndex !== i
                  );

                  setRowInputs(nextRowInputs);
                  onRemoveInput(index, i);
                }}
                disabled={rowInputs.length === 1}
              />
            </When>
          </FlexContainer>
        );
      })}
      <When condition={allowMultipleInputs}>
        <ButtonWrapper>
          <Button
            severity="low"
            icon="plus"
            onClick={() => {
              setRowInputs((prev) => [...prev, ""]);
              onChangeInput(index, rowInputs);
              onAddInput(index);
            }}
          >
            {addInputButtonText ?? "Add"}
          </Button>
        </ButtonWrapper>
      </When>
    </div>
  );
}

type ExpandableRowProps = {
  index: number;
  style: CSSProperties;
  filteredOptions: ListWithInputsItem[];
  expanded: Record<number, boolean>;
  inputs: Record<number, string[]>;
  isMultiSelectAllowed: boolean;
  toggleRow: (index: number) => void;
  ensureInputs: (index: number) => void;
  changeInput: (rowIndex: number, nextRowInputs: string[]) => void;
  addInput: (index: number, value?: string) => void;
  removeInput: (rowIndex: number, inputIndex: number) => void;
  isCheckboxSelected: (index: number) => boolean;
  selectCheckbox: Dispatch<SetStateAction<Record<number, boolean>>>;
  collapseAllRows: () => void;
};

function ExpandableRow({
  index,
  style,
  filteredOptions,
  expanded,
  inputs,
  isMultiSelectAllowed,
  toggleRow,
  ensureInputs,
  changeInput,
  addInput,
  removeInput,
  isCheckboxSelected,
  selectCheckbox,
  collapseAllRows,
}: ExpandableRowProps) {
  const { label, subLabel, value, noInputs } = filteredOptions[index];

  return (
    <div style={style}>
      <SidebarItemWrapperGrid
        key={value}
        hideBottomBorder
        title={label ?? ""}
        onPointerDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          if (!noInputs) {
            toggleRow(index);
            ensureInputs(index);
          }

          if (noInputs && !isMultiSelectAllowed) {
            collapseAllRows();
          }

          selectCheckbox((prev) => {
            const keys = Object.keys(prev);
            if (!isMultiSelectAllowed && keys.length > 0) {
              const tmp = unselectAll(prev);

              return {
                ...tmp,
                [index]: !prev[index],
              };
            }

            return {
              ...prev,
              [index]: !prev[index],
            };
          });
        }}
      >
        <div>
          <Checkbox withoutLabel readOnly checked={isCheckboxSelected(index)} />
        </div>
        <FlexContainer direction="column" alignItems="start">
          <Text align="left" mb={0} color={theme.color.text.text01}>
            {isNil(label) ? label : trimText(label, 35)}
          </Text>
          <If condition={subLabel}>
            <Then>
              <Text
                align="left"
                mb={0}
                size="sm"
                color={theme.color.text.text01}
              >
                {subLabel}
              </Text>
            </Then>
          </If>
        </FlexContainer>
      </SidebarItemWrapperGrid>
      <When condition={expanded[index] && !noInputs}>
        <RowInputs
          index={index}
          initialInputs={inputs[index] ?? [""]}
          filteredOptions={filteredOptions}
          onChangeInput={changeInput}
          onAddInput={addInput}
          onRemoveInput={removeInput}
        />
      </When>
    </div>
  );
}

type Props = {
  allowModeChange?: boolean;
  isExcludeSelected?: boolean;
  isMultiSelectAllowed: boolean;
  showSearchBar?: boolean;
  selectableItems: ListWithInputsItem[];
  selectedOptions: ListWithInputsSelections;
  onModeChange?: (mode: Mode) => void;
  onMultiSelect: (selections: ListWithInputsSelections) => void;
};

export function SelectableListWithInput({
  allowModeChange = false,
  isExcludeSelected = false,
  isMultiSelectAllowed = true,
  showSearchBar = true,
  selectableItems,
  selectedOptions,
  onModeChange = noop,
  onMultiSelect,
}: Props) {
  const filterHeaderRef =
    useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement | null>;

  const listRef = useRef<List | null>(null);

  const {
    inputs,
    checkboxes,
    filteredOptions,
    isCheckboxSelected,
    setInputs,
    setCheckboxes,
    setSearch,
  } = useSelectableListWithInputsFilter({
    selectableItems,
    selectedOptions,
    onMultiSelect,
  });

  const [selectableListWidth, setSelectableListWidth] = useState(0);

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setSelectableListWidth(filterHeaderRef?.current?.offsetWidth ?? 0);
  }, [filterHeaderRef, setSelectableListWidth]);

  useResize(filterHeaderRef, setSelectableListWidth);

  useEffect(() => {
    const selectedCheckboxes: Record<number, boolean> =
      selectedOptions.checkboxes.reduce((acc, checkboxValue) => {
        const index = selectableItems.findIndex(
          (item) => item.value === checkboxValue
        );

        return {
          ...acc,
          [index]: true,
        };
      }, {});

    Object.keys(selectedCheckboxes).forEach((selectedCheckboxIndex) => {
      listRef?.current?.resetAfterIndex(Number(selectedCheckboxIndex));
    });

    setExpanded(selectedCheckboxes);
  }, [selectedOptions]);

  useEffect(() => {
    if (!isMultiSelectAllowed) {
      Object.keys(selectableItems).forEach((checkboxIndex) => {
        listRef?.current?.resetAfterIndex(Number(checkboxIndex), true);
      });
    }
  }, [checkboxes, isMultiSelectAllowed, selectableItems, selectedOptions]);

  const calculateInputsHeight = (index: number) => {
    const createdInputs = inputs[index] ?? [""]; // one input should be by default
    const { allowMultipleInputs } = filteredOptions[index];

    const inputHeights =
      createdInputs?.reduce((acc) => {
        return acc + INPUT_HEIGHT;
      }, 0) ?? 0;

    if (allowMultipleInputs) {
      return inputHeights + ADD_INPUT_BUTTON_HEIGHT;
    }

    return inputHeights;
  };

  const calculateExpandableRowHeight = (index: number): number => {
    const { subLabel, noInputs } = filteredOptions[index];

    if (expanded[index] && !noInputs) {
      if (subLabel) {
        return ROW_HEIGHT_WITH_SUBLABEL + calculateInputsHeight(index);
      }
      return ROW_HEIGHT + calculateInputsHeight(index);
    }

    if (subLabel) {
      return ROW_HEIGHT_WITH_SUBLABEL;
    }

    return ROW_HEIGHT;
  };

  const calculateListHeight = (): number => {
    const totalHeight = filteredOptions.reduce((sum, _, index) => {
      return sum + calculateExpandableRowHeight(index);
    }, 0);
    return Math.min(totalHeight, MAX_LIST_HEIGHT);
  };

  const toggleRow = (index: number) => {
    setExpanded((prev) => {
      if (!isMultiSelectAllowed) {
        const tmp = unselectAll(prev);

        return {
          ...tmp,
          [index]: !prev[index],
        };
      }

      const next = { ...prev, [index]: !prev[index] };

      listRef?.current?.resetAfterIndex(index, false);
      return next;
    });
  };

  const collapseAllRows = () => {
    setExpanded((prev) => {
      return unselectAll(prev);
    });
  };

  const ensureInputs = (index: number) => {
    setInputs((prev) => {
      return {
        ...prev,
        [index]: prev[index] ? prev[index] : [""],
      };
    });
  };

  const addInput = (index: number, value = "") => {
    setInputs((prev) => {
      const next = {
        ...prev,
        [index]: [...(prev[index] ?? []), value],
      };

      listRef?.current?.resetAfterIndex(index, false);
      return next;
    });
  };

  const removeInput = (rowIndex: number, inputIndex: number) => {
    setInputs((prev) => {
      const currentInputs = prev[rowIndex] ?? [];

      const nextRowInputs = currentInputs.filter(
        (_, index) => index !== inputIndex
      );

      const next = {
        ...prev,
        [rowIndex]: nextRowInputs,
      };

      listRef?.current?.resetAfterIndex(rowIndex, false);

      return next;
    });
  };

  const changeInput = (rowIndex: number, nextRowInputs: string[]) => {
    setInputs((prev) => ({
      ...prev,
      [rowIndex]: nextRowInputs,
    }));
  };

  return (
    <>
      <div ref={filterHeaderRef}>
        <FilterHeader
          showSearchBar={showSearchBar}
          allowModeChange={allowModeChange}
          defaultMode={getSelectedMode(isExcludeSelected)}
          onSetSearch={setSearch}
          onModeChange={onModeChange}
        />
      </div>
      <FlexContainer direction="column">
        <List
          ref={listRef}
          height={calculateListHeight()}
          itemCount={filteredOptions.length}
          itemSize={calculateExpandableRowHeight}
          width={selectableListWidth}
        >
          {({ index, style }) => (
            <ExpandableRow
              index={index}
              style={style}
              isMultiSelectAllowed={isMultiSelectAllowed}
              filteredOptions={filteredOptions}
              expanded={expanded}
              inputs={inputs}
              toggleRow={toggleRow}
              ensureInputs={ensureInputs}
              changeInput={changeInput}
              addInput={addInput}
              removeInput={removeInput}
              selectCheckbox={setCheckboxes}
              isCheckboxSelected={isCheckboxSelected}
              collapseAllRows={collapseAllRows}
            />
          )}
        </List>
      </FlexContainer>
    </>
  );
}

const InputWrapper = styled.div`
  flex: 1;
`;

const ButtonWrapper = styled.div`
  padding: ${theme.spacing.spacing02};
`;
